import { Box, Button, Center, Flex, Icon, Text } from '@chakra-ui/react'
import React, { Component, ReactNode } from 'react'
import { SadFace } from '../../utils/constants/icons'

interface Props {
  children: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: any
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null }
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary]', error, errorInfo.componentStack)
    this.setState({ errorInfo })
  }

  render() {
    if (this.state.hasError) {
      return (
        <Center h="calc(100vh - 300px)" flexDirection="column">
          <Box maxW="500px" textAlign="center" p="32px">
            <Center mb="26px">
              <Icon width="47px" height="48px" viewBox="0 0 47 48" color="red.500">
                <SadFace />
              </Icon>
            </Center>

            <Text mb="16px" color="neutral.900" textStyle="h5">
              Something went wrong
            </Text>

            <Text color="neutral.500" textStyle="b-sm" mb="24px" fontWeight="400">
              An unexpected error occurred while loading this page. Please try refreshing the page or contact support if
              the problem persists.
            </Text>

            {this.state.error && (
              <Box
                bg="red.50"
                border="1px"
                borderColor="red.200"
                borderRadius="8px"
                p="16px"
                mb="24px"
                textAlign="left"
              >
                <Text textStyle="b-sm" fontWeight="500" color="red.700" mb="8px">
                  Error Details:
                </Text>
                <Text textStyle="b-xs" color="red.600" fontFamily="mono" whiteSpace="pre-wrap">
                  {this.state.error.message}
                </Text>
              </Box>
            )}

            <Flex gap="12px" justifyContent="center">
              <Button variant="outline" onClick={() => window.location.reload()} size="sm">
                Refresh Page
              </Button>
              <Button variant="solid" onClick={() => window.history.back()} size="sm">
                Go Back
              </Button>
            </Flex>
          </Box>
        </Center>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
